import React, { useCallback, useMemo, useState } from 'react'
import { Col, List, Row, Tooltip, Modal } from 'antd'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  Agency,
  PhoneContact,
  Request,
  RequestQuoteVehicle,
  RequestTicketUserData,
  RequestTicketSelectedSituation
} from 'src/types'
import {
  Empty,
  Spacer,
  T,
  Text,
  TextTValue,
  Tstring,
  useGetSelectedVehicles
} from 'src/components/atoms'
import {
  AvatarListHorizontal,
  TagCurrency,
  TagTicketSituation
} from 'src/components/molecules'
import { saveFullRequest } from 'src/core/store/ducks/requests/actions'
import { ButtonTitled } from 'src/components/Ant'
import { getVehiclesHistory } from 'src/core/store/ducks/vehicles/actions'
import ModalHistory from 'src/views/Requests/views/Form/components/Steps/Services/components/vehicles/components/modal-history'
import ModalFormEdit from 'src/views/Requests/views/Form/components/Steps/Services/components/vehicles/components/modal-form/index'
import { OriginRequest } from 'src/core/store/ducks/requests/types'
import { PhoneMask } from 'src/core/utils/phoneMask'
import Images from 'src/config/images'

import * as CSS from './styles'

interface Props {
  request: Request
  approvals?: boolean
  originRequest?: OriginRequest
}

type SelectedVehicle = Pick<
  RequestQuoteVehicle,
  | 'air_conditioning'
  | 'company_image_url'
  | 'company'
  | 'gearbox'
  | 'information'
  | 'model'
  | 'request_quote_vehicle_unique'
  | 'return_location'
  | 'total_value'
  | 'vehicle_image_url'
  | 'withdrawal_location'
> & {
  agency_data?: Agency
  eticket_code?: string
  return_date: string
  return_time: string
  situation: RequestTicketSelectedSituation
  status?: any
  user_data: RequestTicketUserData[]
  withdrawal_date: string
  withdrawal_time: string
}

function RenderItemListVehicles({
  item
}: {
  approvals: boolean
  item: SelectedVehicle
}) {
  const emissionContacts: PhoneContact[] =
    item.agency_data?.agency_organization.contacts?.reduce<PhoneContact[]>(
      (acc, cur) => {
        if (cur.hasOwnProperty('phone_contacts')) {
          acc.push(
            ...{ phone_contacts: null, email_contacts: null, ...cur }
              .phone_contacts
          )
        }
        return acc
      },
      []
    ) || []

  return (
    <CSS.VehicleItem>
      <List.Item.Meta
        description={
          <>
            {/* Top */}
            <CSS.DivBordered>
              <Row>
                <Col span={24}>
                  <CSS.CardTopDiv>
                    <div>
                      <AvatarListHorizontal
                        ownerInfo={`${Tstring(
                          'web.services.vehicle.driver'
                        )}: `}
                        users={item.user_data}
                      />
                    </div>
                    {!!item.status && (
                      <div>
                        <TagTicketSituation status={item.situation} />
                      </div>
                    )}
                    {!!item.eticket_code && (
                      <div>
                        <Spacer horizontal="4px" />
                        <Text
                          strong
                          suffix=":"
                          text="web.services.vehicle.reservation"
                        />
                        <CSS.Code>
                          <span>
                            <Text code copyable={{ text: item.eticket_code }}>
                              {item.eticket_code}
                            </Text>
                          </span>
                        </CSS.Code>
                      </div>
                    )}
                  </CSS.CardTopDiv>
                </Col>
              </Row>
            </CSS.DivBordered>
            {/* Vehicle Infos */}
            <CSS.DivBordered>
              <CSS.CardContentDiv>
                {/* Title */}
                <Row>
                  <Col span={24}>
                    <Tooltip title={item.model}>
                      <CSS.Title>
                        <CSS.TextEllipsis>{item.model}</CSS.TextEllipsis>
                      </CSS.Title>
                    </Tooltip>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <TextTValue text="web.services.vehicle.form.withdrawal.local">
                      {item.withdrawal_location}
                    </TextTValue>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <TextTValue text="web.services.vehicle.form.devolution.local">
                      {item.return_location}
                    </TextTValue>
                  </Col>
                </Row>
                <Spacer vertical="12px" />
                {/* Image */}
                <Row gutter={24}>
                  <Col span={8}>
                    <CSS.Image align="center">
                      <img
                        alt="Vehicle"
                        src={item.vehicle_image_url}
                        style={{ maxWidth: '175px' }}
                      />
                    </CSS.Image>
                  </Col>
                  <Col span={16}>
                    <CSS.Image align="left">
                      <img
                        alt="Vehicle"
                        src={item.company_image_url}
                        style={{ maxWidth: '100px' }}
                      />
                    </CSS.Image>
                    <TextTValue text="web.services.vehicle.form.withdrawal.title">
                      {item.withdrawal_date}
                      <Spacer horizontal="8px" />
                      {item.withdrawal_time}
                    </TextTValue>
                    <TextTValue text="web.services.vehicle.form.devolution.title">
                      {item.return_date}
                      <Spacer horizontal="8px" />
                      {item.return_time}
                    </TextTValue>
                  </Col>
                </Row>
                {/* Infos */}
                <Spacer vertical="12px" />
                <CSS.AdditionalInfo>
                  <div>
                    {item.air_conditioning && (
                      <Text
                        prefix="- "
                        strong
                        text="web.services.vehicle.form.air-conditioning"
                      />
                    )}
                    <TextTValue
                      prefix="- "
                      strong
                      text="web.services.vehicle.gearbox"
                    >
                      {item.gearbox}
                    </TextTValue>
                    <TextTValue
                      prefix="- "
                      strong
                      text="web.services.vehicle.informations"
                    >
                      {item.information}
                    </TextTValue>
                  </div>
                  <div>
                    <TagCurrency value={item.total_value} />
                  </div>
                </CSS.AdditionalInfo>
              </CSS.CardContentDiv>
            </CSS.DivBordered>
            {/* Emission infos */}
            <CSS.DivBordered>
              <CSS.AgencyInfo>
                <div>
                  <TextTValue text="web.agencies.Issuer">
                    {item.agency_data?.agency_organization.trade_name}
                  </TextTValue>
                  {emissionContacts.map(phone_contacts => (
                    <div key={phone_contacts.contact_id}>
                      <Text
                        strong
                        suffix=": "
                        text="web.agencies.contacts.phone"
                      />
                      <Text
                        code
                        copyable={{
                          text: phone_contacts.phone_number
                        }}
                      >
                        {`${phone_contacts.calling_code} ${PhoneMask(
                          phone_contacts.phone_number
                        )}`}
                      </Text>
                      {phone_contacts.is_whatsapp && (
                        <CSS.IconWhatsApp
                          alt="WhatsApp"
                          src={Images.logoWhatsSmall}
                          onClick={() => {
                            window.open(
                              `https://wa.me/${phone_contacts.phone_number}`
                            )
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <TagCurrency value={item.total_value} />
                </div>
              </CSS.AgencyInfo>
            </CSS.DivBordered>
          </>
        }
      />
    </CSS.VehicleItem>
  )
}

function VehiclesSummary({ approvals, request, originRequest }: Props) {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [visibleModalHistory, setVisibleModalHistory] = useState<boolean>(false)

  const selectedVehicles = useGetSelectedVehicles(request)

  const [visibleModalEdit, setVisibleModalEdit] = useState<boolean>(false)

  const handleDeleteTicketsVehicle = useCallback(
    () => {
      Modal.confirm({
        cancelText: t('common.actions.cancel'),
        content: t('web.approvals.modal-remove-vehicle'),
        okText: t('common.actions.yes'),
        onCancel() {},
        onOk: async () => {
          return new Promise(() => {
            dispatch(
              saveFullRequest({
                ...request,
                request_tickets: request.request_tickets.map(ticket =>
                  ticket.request_ticket_type !== 'VEHICLE'
                    ? { ...ticket }
                    : {
                        ...ticket,
                        active: false
                      }
                )
              })
            )
          })
        },
        title: t('common.are-you-sure')
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request]
  )

  const extraButtons = useMemo(() => {
    if (selectedVehicles.length <= 0) {
      return [
        approvals && (
          <ButtonTitled
            icon="plus"
            key="vehicle-new"
            onClick={() => setVisibleModalEdit(true)}
            size="default"
            style={{ marginLeft: 8 }}
            tooltip="common.actions.add"
          />
        )
      ]
    }

    return [
      approvals && (
        <ButtonTitled
          icon="edit"
          key="vehicle-edit"
          onClick={() => setVisibleModalEdit(true)}
          size="default"
          style={{ marginLeft: 8 }}
          tooltip="common.actions.edit"
        />
      ),
      approvals && (
        <ButtonTitled
          icon="delete"
          key="vehicle-delete"
          onClick={handleDeleteTicketsVehicle}
          size="default"
          style={{ marginLeft: 8 }}
          tooltip="common.actions.remove"
        />
      ),
      typeof request.request_id === 'number' && (
        <ButtonTitled
          key="vehicle-history"
          icon="history"
          onClick={() => {
            if (request?.request_id) {
              dispatch(getVehiclesHistory({ request_id: request.request_id }))
            }
            setVisibleModalHistory(true)
          }}
          size="default"
          tooltip="common.actions.historical"
          style={{ marginLeft: 8 }}
        />
      )
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvals, selectedVehicles])

  return (
    <>
      <CSS.Card
        data-test-id="summary-vehicles"
        bodyStyle={{ paddingTop: 0, minHeight: 220 }}
        extra={extraButtons}
        title={<T text="web.services.vehicle.title" />}
      >
        <List
          dataSource={selectedVehicles}
          itemLayout="horizontal"
          locale={{ emptyText: <Empty /> }}
          renderItem={(item, index) => (
            <RenderItemListVehicles
              approvals={approvals}
              item={item}
              key={index}
            />
          )}
        />
      </CSS.Card>
      <ModalHistory
        toggle={() => {
          setVisibleModalHistory(false)
        }}
        visible={visibleModalHistory}
      />
      <ModalFormEdit
        approvals={approvals}
        request={request}
        toggle={() => {
          setVisibleModalEdit(false)
        }}
        visible={visibleModalEdit}
      />
    </>
  )
}

export default VehiclesSummary
